import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MapPin, Pencil, Plus, Search, Trash2 } from "lucide-react";

import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Skeleton } from "../../../components/feedback/Skeleton";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { Pagination } from "../../../components/ui/Pagination";
import { ConfirmDeleteModal } from "../../../components/admin/ConfirmDeleteModal";
import { countryName } from "../../../i18n/countries";
import { adminAddressesService } from "../services/adminAddresses";
import { formatApiError } from "../../../services/api";
import { AdminAddressFormModal } from "../components/AdminAddressFormModal";

/**
 * A12 — global address book (admin view).
 * A18 — creator column, creator filter, complete-column and
 *       incomplete-only toggle all retired. Table now shows the
 *       essentials (company, street, PLZ/city/country) plus row
 *       actions.
 */
export default function AdminAddressesPage() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language?.startsWith("en") ? "en" : "de";

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const [deleting, setDeleting] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, page_size: pageSize };
      if (search.trim()) params.q = search.trim();
      const d = await adminAddressesService.list(params);
      setItems(d.items || []);
      setTotal(d.total || 0);
    } catch (err) {
      setError(formatApiError(err, t("common.error_generic")));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, t]);

  useEffect(() => {
    reload();
  }, [reload]);

  const submit = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await adminAddressesService.update(editing.id, payload);
        toast.success(t("addresses.saved"));
      } else {
        await adminAddressesService.create(payload);
        toast.success(t("addresses.created"));
      }
      setModalOpen(false);
      setEditing(null);
      await reload();
    } catch (err) {
      const code = err?.response?.data?.detail?.code;
      if (code === "DUPLICATE_ADDRESS") toast.error(t("addresses.err_duplicate"));
      else toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    try {
      await adminAddressesService.remove(deleting.id);
      toast.success(t("addresses.deleted"));
      setDeleting(null);
      await reload();
    } catch (err) {
      toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setDeleteBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);

  return (
    <>
      <div className="space-y-6" data-testid="addresses-page">
        <header className="flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-end">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <MapPin className="h-5 w-5 text-champagne" aria-hidden />
              <h1
                className="text-3xl font-semibold tracking-tight text-text-primary"
                data-testid="addresses-title"
              >
                {t("addresses.title")}
              </h1>
            </div>
            <p className="mt-1 text-sm text-text-secondary">
              {t("addresses.subtitle")}
            </p>
          </div>
          <Button
            onClick={() => {
              setEditing(null);
              setModalOpen(true);
            }}
            data-testid="addresses-add-new"
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t("addresses.add_new_cta")}
          </Button>
        </header>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="relative flex-1 max-w-md">
            <Label className="text-xs font-medium uppercase tracking-wide text-text-secondary">
              {t("addresses.search_label")}
            </Label>
            <div className="relative mt-1.5">
              <Search
                className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary"
                aria-hidden
              />
              <Input
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder={t("addresses.search_placeholder")}
                data-testid="addresses-search"
                className="h-10 pl-9 bg-surface border-border-subtle focus-visible:ring-champagne"
              />
            </div>
          </div>
        </div>

        {error ? (
          <ErrorState
            title={t("common.error_generic")}
            body={error}
            onRetry={reload}
            testId="addresses-error"
          />
        ) : loading ? (
          <div className="space-y-2" data-testid="addresses-loading">
            {[...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState
            title={t("addresses.empty_title")}
            body={t("addresses.empty_body")}
            testId="addresses-empty"
          />
        ) : (
          <>
            <div className="overflow-x-auto rounded-lg border border-border-subtle bg-surface shadow-card">
              <table className="min-w-full text-sm">
                <thead className="bg-offwhite text-left text-[11px] uppercase tracking-wide text-text-secondary">
                  <tr>
                    <th className="px-4 py-3 font-semibold">
                      {t("addresses.col_company")}
                    </th>
                    <th className="px-4 py-3 font-semibold">
                      {t("addresses.col_street")}
                    </th>
                    <th className="px-4 py-3 font-semibold">
                      {t("addresses.col_postal")}
                    </th>
                    <th className="px-4 py-3 font-semibold">
                      {t("addresses.col_city")}
                    </th>
                    <th className="px-4 py-3 font-semibold">
                      {t("addresses.col_country")}
                    </th>
                    <th className="px-4 py-3 text-right font-semibold">
                      {t("common.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border-subtle">
                  {items.map((a) => (
                    <tr
                      key={a.id}
                      data-testid={`address-row-${a.id}`}
                      className="hover:bg-offwhite/50"
                    >
                      <td className="px-4 py-3">
                        <div className="truncate max-w-[16rem] text-text-primary">
                          {a.company}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-text-secondary">
                        <div className="max-w-[18rem] truncate">{a.street}</div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                        {a.postal_code}
                      </td>
                      <td className="px-4 py-3 text-text-primary">{a.city}</td>
                      <td className="px-4 py-3 text-text-secondary">
                        {countryName(a.country, lng)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(a);
                              setModalOpen(true);
                            }}
                            data-testid={`address-edit-${a.id}`}
                            aria-label={t("common.edit")}
                            title={t("common.edit")}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-text-primary transition-colors hover:bg-offwhite"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleting(a)}
                            data-testid={`address-delete-${a.id}`}
                            aria-label={t("common.delete")}
                            title={t("common.delete")}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-status-unavailable transition-colors hover:bg-status-unavailable/10"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(n) => {
                setPageSize(n);
                setPage(1);
              }}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              total={total}
              testId="addresses-pagination"
            />
          </>
        )}
      </div>

      <AdminAddressFormModal
        open={modalOpen}
        onOpenChange={(v) => {
          if (!v) {
            setModalOpen(false);
            setEditing(null);
          }
        }}
        initial={editing}
        onSubmit={submit}
        submitting={saving}
      />

      <ConfirmDeleteModal
        open={Boolean(deleting)}
        onClose={() => setDeleting(null)}
        onConfirm={confirmDelete}
        name={
          deleting
            ? `${deleting.company} — ${deleting.city || ""}`.trim()
            : ""
        }
        busy={deleteBusy}
        testId="addresses-delete"
      />
    </>
  );
}
